import React, { useState, useEffect } from 'react';
import { 
  Save, 
  Upload, 
  X, 
  AlertCircle, 
  CheckCircle, 
  Image as ImageIcon,
  Trash2,
  Plus,
  Minus,
  GripVertical
} from 'lucide-react';

const Site = () => {
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Hero Section State
  const [heroImages, setHeroImages] = useState([]);
  const [newHeroImages, setNewHeroImages] = useState([]);
  const [products, setProducts] = useState([]);
  const [heroImageLinks, setHeroImageLinks] = useState({});
  
  // Top Bar Offer State
  const [topBarOffer, setTopBarOffer] = useState({
    enabled: false,
    text: '',
    background_color: '#000000',
    text_color: '#ffffff',
    link_url: ''
  });

  // Product Handle Instructions State
  const [instructions, setInstructions] = useState([]);

  const API_BASE_URL = 'https://petifyshop.mycartly.in';
  const STORE_ID = '1532365'; // Store ID for hero images path

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token found');
      setError('Authentication required. Please login again.');
      return {};
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const getMultipartHeaders = () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token found');
      setError('Authentication required. Please login again.');
      return {};
    }
    return {
      'Authorization': `Bearer ${token}`,
      // Don't set Content-Type for FormData - browser will set it with boundary
    };
  };

  useEffect(() => {
    loadSiteSettings();
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/admin/products/list`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setProducts(data.products || []);
        }
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const loadSiteSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Authentication required. Please login again.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/admin/site-settings`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.success) {
          // Load hero images
          const heroImagesData = data.data.hero_images || [];
          setHeroImages(heroImagesData);
          
          // Initialize hero image links
          const links = {};
          heroImagesData.forEach((image, index) => {
            if (typeof image === 'object' && image.filename) {
              links[image.filename] = {
                product_id: image.product_id || '',
                alt_text: image.alt_text || `Hero Image ${index + 1}`
              };
            } else {
              // Legacy format
              links[image] = {
                product_id: '',
                alt_text: `Hero Image ${index + 1}`
              };
            }
          });
          setHeroImageLinks(links);
          
          // Load top bar offer
          setTopBarOffer(data.data.top_bar_offer || {
            enabled: false,
            text: '',
            background_color: '#000000',
            text_color: '#ffffff',
            link_url: ''
          });
          
          // Load product instructions
          setInstructions(data.data.product_instructions || []);
        }
      } else {
        const errorData = await response.json();
        console.error('API Error:', errorData);
        setError(errorData.msg || errorData.error || 'Failed to load site settings');
      }
    } catch (error) {
      console.error('Network error:', error);
      setError('Failed to load site settings');
    }
    setLoading(false);
  };

  const handleHeroImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length > 5) {
      setError('Maximum 5 hero images allowed');
      return;
    }

    const validFiles = [];
    for (let file of files) {
      if (file.size > 2 * 1024 * 1024) { // 2MB
        setError(`Image "${file.name}" is too large. Maximum size is 2MB`);
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setError(`"${file.name}" is not a valid image file`);
        return;
      }
      
      validFiles.push(file);
    }

    setNewHeroImages(validFiles);
    setError('');
  };

  const removeNewHeroImage = (index) => {
    setNewHeroImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingHeroImage = (index) => {
    setHeroImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleHeroImageLinkChange = (filename, field, value) => {
    setHeroImageLinks(prev => ({
      ...prev,
      [filename]: {
        ...prev[filename],
        [field]: value
      }
    }));
  };

  const addInstruction = () => {
    setInstructions(prev => [...prev, '']);
  };

  const removeInstruction = (index) => {
    setInstructions(prev => prev.filter((_, i) => i !== index));
  };

  const updateInstruction = (index, value) => {
    setInstructions(prev => prev.map((inst, i) => i === index ? value : inst));
  };

  const moveInstruction = (index, direction) => {
    if (direction === 'up' && index > 0) {
      setInstructions(prev => {
        const newInstructions = [...prev];
        [newInstructions[index], newInstructions[index - 1]] = [newInstructions[index - 1], newInstructions[index]];
        return newInstructions;
      });
    } else if (direction === 'down' && index < instructions.length - 1) {
      setInstructions(prev => {
        const newInstructions = [...prev];
        [newInstructions[index], newInstructions[index + 1]] = [newInstructions[index + 1], newInstructions[index]];
        return newInstructions;
      });
    }
  };

  const handleTopBarOfferChange = (field, value) => {
    setTopBarOffer(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (topBarOffer.enabled && !topBarOffer.text.trim()) {
      setError('Top bar offer text is required when enabled');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaveLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Authentication required. Please login again.');
        setSaveLoading(false);
        return;
      }

      const submitData = new FormData();
      
      // Add top bar offer data
      submitData.append('top_bar_offer', JSON.stringify(topBarOffer));
      
      // Add product instructions
      const filteredInstructions = instructions.filter(inst => inst.trim() !== '');
      submitData.append('product_instructions', JSON.stringify(filteredInstructions));
      
      // Add hero images if any
      if (newHeroImages.length > 0) {
        newHeroImages.forEach((image, index) => {
          submitData.append('hero_images', image);
        });
      }
      
      // Add existing hero images info (for deletion handling)
      submitData.append('existing_hero_count', heroImages.length.toString());
      
      // Add hero image links
      const heroLinksArray = Object.entries(heroImageLinks).map(([filename, linkData]) => ({
        filename,
        product_id: linkData.product_id || null,
        alt_text: linkData.alt_text || ''
      }));
      submitData.append('hero_image_links', JSON.stringify(heroLinksArray));

      // Debug FormData

      const response = await fetch(`${API_BASE_URL}/admin/site-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type for FormData
        },
        body: submitData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Site settings updated successfully!');
        
        // Update hero images if new ones were uploaded
        if (data.hero_images) {
          setHeroImages(data.hero_images);
        }
        
        // Clear new images
        setNewHeroImages([]);
        
        // Clear file input
        const fileInput = document.getElementById('hero-images-upload');
        if (fileInput) {
          fileInput.value = '';
        }
        
      } else {
        setError(data.msg || data.error || 'Failed to update site settings');
      }
    } catch (error) {
      console.error('Submit error:', error);
      setError('Network error occurred. Please check your connection and try again.');
    }
    setSaveLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Site Settings</h1>
        <button
          onClick={handleSubmit}
          disabled={saveLoading}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saveLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <Save className="h-4 w-4" />
          )}
          <span>{saveLoading ? 'Saving...' : 'Save Changes'}</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
          <span className="text-red-700 text-sm">{error}</span>
          <button
            onClick={() => setError('')}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center">
          <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
          <span className="text-green-700 text-sm">{success}</span>
          <button
            onClick={() => setSuccess('')}
            className="ml-auto text-green-500 hover:text-green-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Hero Section Images */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Hero Section Image Slider</h2>
          
          {/* Current Hero Images */}
          {heroImages.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Current Hero Images ({heroImages.length}/5)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {heroImages.map((image, index) => {
                  const filename = typeof image === 'object' ? image.filename : image;
                  const currentLink = heroImageLinks[filename] || { product_id: '', alt_text: `Hero Image ${index + 1}` };
                  
                  return (
                    <div key={index} className="relative group bg-gray-50 rounded-lg p-4">
                      <div className="relative">
                        <img
                          src={`${API_BASE_URL}/uploads/hero/${STORE_ID}/${filename}?t=${Date.now()}`}
                          alt={currentLink.alt_text}
                          className="w-full h-32 object-cover rounded-lg border"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-full h-32 bg-gray-100 rounded-lg hidden items-center justify-center border">
                          <ImageIcon className="h-8 w-8 text-gray-400" />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeExistingHeroImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors shadow-sm opacity-0 group-hover:opacity-100"
                          title="Remove image"
                        >
                          <X className="h-3 w-3" />
                        </button>
                        <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                          {index + 1}
                        </div>
                      </div>
                      
                      {/* Product Link Configuration */}
                      <div className="mt-3 space-y-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Link to Product
                          </label>
                          <select
                            value={currentLink.product_id || ''}
                            onChange={(e) => handleHeroImageLinkChange(filename, 'product_id', e.target.value)}
                            className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                          >
                            <option value="">No product link</option>
                            {products.map((product) => (
                              <option key={product.product_id} value={product.product_id}>
                                {product.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Alt Text
                          </label>
                          <input
                            type="text"
                            value={currentLink.alt_text || ''}
                            onChange={(e) => handleHeroImageLinkChange(filename, 'alt_text', e.target.value)}
                            className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                            placeholder="Hero Image description"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Upload New Hero Images */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Upload Hero Images (Max 5, 2MB each)
            </label>
            
            {!newHeroImages.length ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleHeroImageChange}
                  className="hidden"
                  id="hero-images-upload"
                  disabled={saveLoading}
                />
                <label
                  htmlFor="hero-images-upload"
                  className="cursor-pointer flex flex-col items-center space-y-2"
                >
                  <Upload className="h-8 w-8 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    Click to upload hero images
                  </span>
                  <span className="text-xs text-gray-500">
                    PNG, JPG, JPEG, GIF, WEBP up to 2MB each
                  </span>
                </label>
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-600 mb-3">New Images Preview ({newHeroImages.length}/5):</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {newHeroImages.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={URL.createObjectURL(image)}
                        alt={`New Hero ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={() => removeNewHeroImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors shadow-sm opacity-0 group-hover:opacity-100"
                      >
                        <X className="h-3 w-3" />
                      </button>
                      <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                        {index + 1}
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setNewHeroImages([]);
                    const fileInput = document.getElementById('hero-images-upload');
                    if (fileInput) fileInput.value = '';
                  }}
                  className="mt-3 text-sm text-blue-600 hover:text-blue-800"
                >
                  Upload different images
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Top Bar Offer */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Bar Offer</h2>
          
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="offer-enabled"
                checked={topBarOffer.enabled}
                onChange={(e) => handleTopBarOfferChange('enabled', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
              />
              <label htmlFor="offer-enabled" className="text-sm font-medium text-gray-700">
                Enable top bar offer
              </label>
            </div>

            {topBarOffer.enabled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Offer Text *
                  </label>
                  <input
                    type="text"
                    value={topBarOffer.text}
                    onChange={(e) => handleTopBarOfferChange('text', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Free shipping on orders over ₹999!"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Background Color
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={topBarOffer.background_color}
                        onChange={(e) => handleTopBarOfferChange('background_color', e.target.value)}
                        className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={topBarOffer.background_color}
                        onChange={(e) => handleTopBarOfferChange('background_color', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Text Color
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={topBarOffer.text_color}
                        onChange={(e) => handleTopBarOfferChange('text_color', e.target.value)}
                        className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={topBarOffer.text_color}
                        onChange={(e) => handleTopBarOfferChange('text_color', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Link URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={topBarOffer.link_url}
                    onChange={(e) => handleTopBarOfferChange('link_url', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://example.com/offer"
                  />
                </div>

                {/* Preview */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preview:</label>
                  <div
                    className="w-full py-2 px-4 text-center text-sm font-medium"
                    style={{
                      backgroundColor: topBarOffer.background_color,
                      color: topBarOffer.text_color
                    }}
                  >
                    {topBarOffer.text || 'Your offer text will appear here'}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Product Handle Instructions */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Product Handle Instructions</h2>
            <button
              type="button"
              onClick={addInstruction}
              className="flex items-center space-x-2 bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 transition-colors text-sm"
            >
              <Plus className="h-4 w-4" />
              <span>Add Instruction</span>
            </button>
          </div>

          <div className="space-y-3">
            {instructions.length > 0 ? (
              instructions.map((instruction, index) => (
                <div key={index} className="flex items-center space-x-2 group">
                  <div className="flex items-center space-x-1">
                    <button
                      type="button"
                      onClick={() => moveInstruction(index, 'up')}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      title="Move up"
                    >
                      <GripVertical className="h-4 w-4" />
                    </button>
                    <span className="text-sm font-medium text-gray-500 w-6">
                      {index + 1}.
                    </span>
                  </div>
                  
                  <input
                    type="text"
                    value={instruction}
                    onChange={(e) => updateInstruction(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter instruction point..."
                  />
                  
                  <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={() => moveInstruction(index, 'down')}
                      disabled={index === instructions.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      title="Move down"
                    >
                                             Down
                    </button>
                    <button
                      type="button"
                      onClick={() => removeInstruction(index)}
                      className="p-1 text-red-500 hover:text-red-700"
                      title="Remove instruction"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">No instructions added yet.</p>
                <p className="text-xs mt-1">Click "Add Instruction" to get started.</p>
              </div>
            )}
          </div>

          {instructions.length > 0 && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Preview:</h4>
              <ol className="list-decimal list-inside space-y-1">
                {instructions
                  .filter(inst => inst.trim() !== '')
                  .map((instruction, index) => (
                  <li key={index} className="text-sm text-gray-600">
                    {instruction}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default Site;